import { installedApps } from "@norn/widget-renderer/model"
import { Link, useLocation, useParams } from "@tanstack/react-router"
import {
  AppWindow,
  Boxes,
  ChevronRight,
  Database,
  Home,
  Inbox,
  KeyRound,
  LayoutGrid,
  ListTree,
  type LucideIcon,
  Map as MapIcon,
  Network,
  NotebookTabs,
  ChartColumn,
  FileInput,
  LayoutDashboard,
  Search,
  Shapes,
  ShieldAlert,
  ShieldCheck,
  TableProperties,
} from "lucide-react"
import { GraphObjectSearch } from "@/components/graph/object-search"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Tooltip } from "@/components/ui/tooltip"
import type { MessageKey } from "@/i18n/en"
import type { Scope } from "@/lib/connection"
import { type ConsoleAppGroup, consoleAppGroups } from "@/lib/console-nav"
import { kindLabel } from "@/lib/defs"
import { useFormatNumber, useLocale, useT } from "@/lib/i18n"
import { useDefinitions } from "@/lib/queries"
import { cn } from "@/lib/utils"
import { openCommandPalette } from "@/workspace/command-palette"
import { rememberMode } from "@/workspace/mode"
import { useOntology } from "@/workspace/hooks"
import { NavUser } from "./nav-user"
import { NornMark } from "./norn-mark"
import { ScopeSwitcher } from "./scope-switcher"
import { useShellNav } from "./shell-nav"

const row =
  "flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/70 hover:text-foreground data-[status=active]:bg-primary/10 data-[status=active]:font-medium data-[status=active]:text-primary [&_svg]:size-4 [&_svg]:shrink-0"
const destinations = [
  { path: "/$orgId/$environmentId/builder", label: "nav.overview", icon: LayoutGrid },
  { path: "/$orgId/$environmentId/apps", label: "nav.apps", icon: AppWindow },
  { path: "/$orgId/$environmentId/definitions", label: "nav.definitions", icon: Boxes },
  { path: "/$orgId/$environmentId/explorer", label: "nav.explorer", icon: Network },
  { path: "/$orgId/$environmentId/objects", label: "nav.objects", icon: Database },
  { path: "/$orgId/$environmentId/ledger", label: "nav.ledger", icon: ListTree },
  { path: "/$orgId/$environmentId/inbox", label: "nav.inbox", icon: Inbox },
  { path: "/$orgId/$environmentId/findings", label: "nav.findings", icon: ShieldAlert },
  { path: "/$orgId/$environmentId/permissions", label: "nav.permissions", icon: KeyRound },
] as const satisfies readonly { path: string; label: MessageKey; icon: LucideIcon }[]

const WORKSPACE_SECTIONS = new Set(["", "areas", "records"])

/** Workspace (the business, by meaning) or Builder (the control plane), read from the route. */
export function useShellMode(scope: Scope): "workspace" | "builder" {
  const { pathname } = useLocation()
  const prefix = `/${scope.orgId}/${scope.environmentId}`
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname
  const section = rest.replace(/^\/+/, "").split("/")[0] ?? ""
  return WORKSPACE_SECTIONS.has(decodeURIComponent(section)) ? "workspace" : "builder"
}

/** The one switch between the business workspace and the control plane, always one click away. */
function ModeSwitch({ scope, mode, collapsed }: { scope: Scope; mode: "workspace" | "builder"; collapsed: boolean }) {
  const t = useT()
  const options = [
    { id: "workspace", to: "/$orgId/$environmentId", label: t("ws.mode.workspace"), icon: Home },
    { id: "builder", to: "/$orgId/$environmentId/data", label: t("ws.mode.builder"), icon: Database },
  ] as const
  if (collapsed) {
    const other = options.find((o) => o.id !== mode) ?? options[0]
    return (
      <Tooltip content={other.label}>
        <Link to={other.to} params={scope} aria-label={other.label} onClick={() => rememberMode(other.id === "builder" ? "data" : "business")} className={cn(row, "justify-center px-0")}>
          <other.icon />
        </Link>
      </Tooltip>
    )
  }
  return (
    <div role="group" aria-label={t("ws.mode.label")} className="grid grid-cols-2 gap-0.5 rounded-full border border-border p-0.5">
      {options.map((o) => (
        <Link
          key={o.id}
          to={o.to}
          params={scope}
          aria-current={mode === o.id ? "page" : undefined}
          activeOptions={{ exact: true }}
          onClick={() => rememberMode(o.id === "builder" ? "data" : "business")}
          className={cn(
            "flex h-7 items-center justify-center gap-1.5 rounded-full text-xs font-medium transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/30 [&_svg]:size-3.5",
            mode === o.id ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          <o.icon aria-hidden />
          {o.label}
        </Link>
      ))}
    </div>
  )
}

function SearchButton({ collapsed }: { collapsed: boolean }) {
  const t = useT()
  if (collapsed) {
    return (
      <Tooltip content={t("ws.search.open")}>
        <button type="button" onClick={openCommandPalette} aria-label={t("ws.search.open")} className={cn(row, "justify-center px-0")}>
          <Search />
        </button>
      </Tooltip>
    )
  }
  return (
    <button
      type="button"
      onClick={openCommandPalette}
      className="flex h-9 w-full items-center gap-2 rounded-full border border-border bg-background/40 px-3 text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30"
    >
      <Search className="size-4" aria-hidden />
      <span className="flex-1 text-start">{t("ws.search.open")}</span>
      <kbd className="font-sans text-2xs text-subtle-foreground">⌘K</kbd>
    </button>
  )
}

/** The Data workspace: the analyst's lenses on the same cell, then the control plane's internals. */
function DataNav({ scope, collapsed }: { scope: Scope; collapsed: boolean }) {
  const t = useT()
  const items = [
    { to: "/$orgId/$environmentId/data", label: t("dv.nav.summary"), icon: LayoutDashboard, exact: true },
    { to: "/$orgId/$environmentId/data/ontology", label: t("dv.nav.ontology"), icon: Shapes, exact: false },
    { to: "/$orgId/$environmentId/data/workbooks", label: t("wb.nav"), icon: NotebookTabs, exact: false },
    { to: "/$orgId/$environmentId/data/explore", label: t("dv.nav.explore"), icon: TableProperties, exact: false },
    { to: "/$orgId/$environmentId/graph", label: t("nav.graph"), icon: Network, exact: false },
    { to: "/$orgId/$environmentId/data/metrics", label: t("dv.nav.metrics"), icon: ChartColumn, exact: false },
    { to: "/$orgId/$environmentId/data/sources", label: t("dv.nav.sources"), icon: FileInput, exact: false },
    { to: "/$orgId/$environmentId/data/policy", label: t("dv.nav.policy"), icon: ShieldCheck, exact: false },
  ] as const
  return (
    <div>
      {!collapsed ? <p className="px-3 pb-1.5 text-[11px] font-medium text-muted-foreground">{t("dv.nav.analyze")}</p> : null}
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <Tooltip content={item.label}>
              <Link to={item.to} params={scope} search={{}} activeOptions={{ exact: item.exact }} aria-label={item.label} className={cn(row, collapsed && "justify-center px-0")}>
                <item.icon />
                {!collapsed ? <span className="truncate">{item.label}</span> : null}
              </Link>
            </Tooltip>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The business workspace: home, the area map and each area by name. */
function WorkspaceNav({ scope, collapsed }: { scope: Scope; collapsed: boolean }) {
  const t = useT()
  const formatNumber = useFormatNumber()
  const { ontology } = useOntology(scope)
  const { areaId } = useParams({ strict: false }) as { areaId?: string }
  const top = [
    { to: "/$orgId/$environmentId", label: t("ws.nav.home"), icon: Home, exact: true },
    { to: "/$orgId/$environmentId/areas", label: t("ws.nav.areas"), icon: MapIcon, exact: true },
  ] as const
  return (
    <div className="space-y-5">
      <ul className="space-y-0.5">
        {top.map((item) => (
          <li key={item.to}>
            <Tooltip content={item.label}>
              <Link to={item.to} params={scope} activeOptions={{ exact: item.exact }} aria-label={item.label} className={cn(row, collapsed && "justify-center px-0")}>
                <item.icon />
                {!collapsed ? <span className="truncate">{item.label}</span> : null}
              </Link>
            </Tooltip>
          </li>
        ))}
      </ul>
      {!collapsed && ontology?.areas.length ? (
        <div>
          <p className="px-3 pb-1.5 text-[11px] font-medium text-muted-foreground">{t("ws.nav.businessAreas")}</p>
          <ul className="space-y-0.5">
            {ontology.areas.map((area) => {
              const active = area.id === areaId
              return (
                <li key={area.id}>
                  <Link to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: area.id }} className={cn(row, "h-9")}>
                    <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", area.core ? "border border-muted-foreground" : "bg-muted-foreground/70", active && "bg-primary")} />
                    <span className="truncate">{area.label}</span>
                    <span className="ms-auto text-2xs text-subtle-foreground tabular-nums">{formatNumber(area.types.length)}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/** One disclosure per installed app; page rows belong behind its indent, never on the icon rail. */
function AppNavigation({
  group,
  scope,
  active,
  collapsed,
  entryPageId,
}: {
  group: ConsoleAppGroup
  scope: Scope
  active: boolean
  collapsed: boolean
  entryPageId: string
}) {
  if (collapsed) {
    return (
      <Tooltip content={group.label}>
        <Link
          to="/$orgId/$environmentId/apps/$appId/$pageId"
          params={{ ...scope, appId: group.appId, pageId: entryPageId }}
          search={{}}
          aria-label={group.label}
          className={cn(row, "justify-center px-0", active && "bg-primary/10 text-primary")}
        >
          <AppWindow />
        </Link>
      </Tooltip>
    )
  }
  return (
    <Collapsible defaultOpen={active} className="group/app">
      <CollapsibleTrigger className={cn(row, active && "font-medium text-primary")}>
        <AppWindow />
        <span className="min-w-0 truncate">{group.label}</span>
        <ChevronRight className="ms-auto transition-transform group-data-[panel-open]/app:rotate-90 rtl:-scale-x-100" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="ms-5 mt-0.5 space-y-0.5 border-s border-border ps-3">
          {group.pages.map((page) => (
            <li key={page.pageId}>
              <Link
                to="/$orgId/$environmentId/apps/$appId/$pageId"
                params={{ ...scope, appId: group.appId, pageId: page.pageId }}
                search={{}}
                className="flex h-9 items-center rounded-md px-2 text-[13px] text-muted-foreground hover:bg-accent/70 hover:text-foreground data-[status=active]:bg-primary/10 data-[status=active]:font-medium data-[status=active]:text-primary"
              >
                <span className="truncate">{page.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Console's grouped transparent rail. Installed app declarations own product navigation;
 * the control plane remains reachable independently of any admitted app or definition read. */
export function AppSidebar({ scope, drawer = false }: { scope: Scope; drawer?: boolean }) {
  const t = useT()
  const { preference } = useLocale()
  const formatNumber = useFormatNumber()
  const defs = useDefinitions(scope)
  const { appId: activeAppId } = useParams({ strict: false })
  const apps = defs.data ? installedApps(defs.data) : []
  const groups = defs.data ? consoleAppGroups(defs.data, preference.locale) : []
  const nav = useShellNav()
  const collapsed = !drawer && Boolean(nav?.collapsed)
  const mode = useShellMode(scope)
  return (
    <aside
      data-slot="console-chrome-sidebar"
      data-state={collapsed ? "collapsed" : "expanded"}
      className={cn(
        // On short screens (a phone in landscape) the whole rail scrolls as one column.
        "flex h-full shrink-0 flex-col bg-canvas px-2 py-3 [@media(max-height:560px)]:overflow-y-auto",
        drawer ? "w-full" : collapsed ? "w-16" : "w-66",
      )}
    >
      <Link
        to="/$orgId/$environmentId"
        params={scope}
        activeOptions={{ exact: true }}
        aria-label={t("app.name")}
        className={cn("mb-4 flex h-10 items-center gap-2.5 rounded-lg px-3", collapsed && "justify-center px-0")}
      >
        <NornMark className="size-6 shrink-0" />
        {!collapsed ? <span className="text-base font-semibold">{t("app.name")}</span> : null}
      </Link>
      <div className={cn("space-y-2 pb-4", !collapsed && "px-1")}>
        <ModeSwitch scope={scope} mode={mode} collapsed={collapsed} />
        <SearchButton collapsed={collapsed} />
      </div>
      {!collapsed ? (
        <div className="px-1 pb-3">
          <ScopeSwitcher scope={scope} />
          {mode === "builder" ? (
            <div className="mt-2">
              <GraphObjectSearch />
            </div>
          ) : null}
        </div>
      ) : null}
      <nav aria-label={t("nav.primary")} className="min-h-0 flex-1 space-y-5 overflow-y-auto [@media(max-height:560px)]:min-h-fit [@media(max-height:560px)]:flex-none [@media(max-height:560px)]:overflow-visible">
        {mode === "workspace" ? <WorkspaceNav scope={scope} collapsed={collapsed} /> : null}
        {mode === "builder" ? <DataNav scope={scope} collapsed={collapsed} /> : null}
        {mode === "builder" && groups.length ? (
          <div className="space-y-0.5">
            {groups.map((group) => {
              const active = group.appId === activeAppId
              const declaredEntry = apps.find((app) => app.appId === group.appId)?.ux.app?.entryPageId
              const entryPageId =
                group.pages.find((page) => page.pageId === declaredEntry)?.pageId ?? group.pages[0]?.pageId
              if (!entryPageId) return null
              return (
                <AppNavigation
                  key={`${group.appId}/${active}`}
                  group={group}
                  scope={scope}
                  active={active}
                  collapsed={collapsed}
                  entryPageId={entryPageId}
                />
              )
            })}
          </div>
        ) : null}
        {mode === "builder" ? (
        <div>
          {!collapsed ? (
            <p className="px-3 pb-1.5 text-[11px] font-medium text-muted-foreground">{t("dv.nav.internals")}</p>
          ) : null}
          <ul className="space-y-0.5">
            {destinations.map((item) => (
              <li key={item.path}>
                <Tooltip content={t(item.label)}>
                  <Link
                    to={item.path}
                    params={scope}
                    search={{}}
                    activeOptions={{ exact: item.label === "nav.overview" }}
                    aria-label={t(item.label)}
                    className={cn(row, collapsed && "justify-center px-0")}
                  >
                    <item.icon />
                    {!collapsed ? <span className="truncate">{t(item.label)}</span> : null}
                  </Link>
                </Tooltip>
                {item.label === "nav.definitions" && !collapsed && defs.data?.kinds.length ? (
                  <ul className="ms-5 mt-0.5 space-y-0.5 border-s border-border ps-3">
                    {defs.data.kinds.map((kind) => (
                      <li key={kind.kind}>
                        <Link
                          to="/$orgId/$environmentId/definitions/$kind"
                          params={{ ...scope, kind: kind.kind }}
                          className="flex h-8 items-center gap-2 rounded-md px-2 text-xs text-muted-foreground hover:bg-accent data-[status=active]:text-primary"
                        >
                          <span className="truncate">{kindLabel(kind.kind, t)}</span>
                          <span className="ms-auto tabular-nums">{formatNumber(kind.total)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
        ) : null}
      </nav>
      <NavUser scope={scope} collapsed={collapsed} />
    </aside>
  )
}
